import { useCallback, useMemo } from "react";
import { isNewsItemForSchool } from "../../../functions/src/api/newsTargeting";
import { NEWS_PATH, userNtfNewsItemReadPath, userNtfNewsReadAtPath } from "../../../functions/src/admin/paths";
import { currentSchoolKey, mainDatabase } from "../../api/firebase";
import { useDatabase, useList } from "../../api/firebaseHooks";
import { NewsItem } from "../../types";

export type NewsListEntry = NewsItem & { id: string };

export function useNewsUnread(uid: string | undefined) {
    const schoolKey = currentSchoolKey;
    const newsSnap = useDatabase(NEWS_PATH);
    const newsList = useList(newsSnap);
    const readAtRaw = useDatabase(uid && schoolKey ? userNtfNewsReadAtPath(uid, schoolKey) : null)?.val();
    const readAtByNewsId = (readAtRaw && typeof readAtRaw === "object" ? readAtRaw : {}) as Record<string, number>;

    const activeNews = useMemo(() => {
        const entries: NewsListEntry[] = [];
        newsList.forEach((snap) => {
            const val = snap.val() as NewsItem | null;
            if (!val || !snap.key || !isNewsItemForSchool(val, schoolKey)) return;
            entries.push({ ...val, id: snap.key });
        });
        return entries.sort((a, b) => (b.publishedAt || 0) - (a.publishedAt || 0));
    }, [newsList, schoolKey]);

    const isItemUnread = useCallback((item: NewsListEntry) => !readAtByNewsId[item.id], [readAtByNewsId]);

    const unreadCount = useMemo(
        () => activeNews.filter((item) => isItemUnread(item)).length,
        [activeNews, isItemUnread]
    );
    const hasUnread = unreadCount > 0;

    const markItemRead = useCallback((newsId: string) => {
        if (!uid || !schoolKey || readAtByNewsId[newsId]) return;
        void mainDatabase.ref(userNtfNewsItemReadPath(uid, schoolKey, newsId)).set(Date.now()).catch((err) =>
            console.warn("Could not mark news item as read:", err)
        );
    }, [uid, schoolKey, readAtByNewsId]);

    return { activeNews, hasUnread, unreadCount, isItemUnread, markItemRead, loading: newsSnap === undefined };
}
